// ImportMediaModal — entrada do gravador via upload de áudio ou vídeo.
//
// Fluxo:
//   1. Aluno solta um arquivo (audio/* ou video/*) no dropzone OU escolhe via
//      <input type=file>. Limite 100MB.
//   2. Se for vídeo: extraímos LOCALMENTE só a trilha de áudio
//      (OfflineAudioContext.decodeAudioData + WAV encode 16kHz mono). O vídeo
//      original NUNCA sai do device. [[feedback-no-video-upload]]
//   3. Aluno preenche metadados mínimos (título, tipo, idioma) — o resto
//      pega default sensato.
//   4. Cria a lecture row (api.createLecture) + sobe o blob de áudio
//      (api.transcribeLecture). Backend roteia entre inline (≤20MB) e
//      Gemini File API (>20MB) automaticamente.
//
// Não armazenamos o arquivo de áudio nem o vídeo original em disco — só
// a transcrição + resumo viram texto no banco.

import { useEffect, useRef, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  FileAudio,
  Film,
  Loader2,
  Upload,
  X
} from "lucide-react";
import {
  api,
  ApiError,
  type CreateLectureInput,
  type LectureKind,
  type LectureFocus
} from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

type Phase = "pick" | "extract" | "form" | "upload" | "done" | "error";

interface PreparedAudio {
  blob: Blob;
  durationSec: number;
  sourceKind: "audio" | "video";
  originalSize: number;
  finalSize: number;
  filename: string;
}

interface FormState {
  title: string;
  kind: LectureKind;
  language: string;
  summaryFocus: LectureFocus;
  theme: string;
}

interface ImportMediaModalProps {
  onClose: () => void;
  onSuccess: (lectureId: string) => void;
}

export function ImportMediaModal({ onClose, onSuccess }: ImportMediaModalProps) {
  const [phase, setPhase] = useState<Phase>("pick");
  const [dragOver, setDragOver] = useState(false);
  const [extractProgress, setExtractProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [prepared, setPrepared] = useState<PreparedAudio | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    title: "",
    kind: "aula",
    language: "pt-BR",
    summaryFocus: "detailed_notes",
    theme: ""
  });
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    setErrMsg(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setErrMsg(
        `Arquivo ${(file.size / 1024 / 1024).toFixed(1)}MB excede o limite de 100MB. Divida em pedaços menores.`
      );
      setPhase("error");
      return;
    }
    const isAudio = file.type.startsWith("audio/");
    const isVideo = file.type.startsWith("video/");
    if (!isAudio && !isVideo) {
      setErrMsg("Formato não suportado. Envie áudio (mp3, wav, m4a, ogg, webm) ou vídeo (mp4, mov, webm).");
      setPhase("error");
      return;
    }
    const baseTitle = file.name.replace(/\.[^.]+$/, "").slice(0, 120);
    setForm((f) => ({ ...f, title: f.title || baseTitle }));

    if (isAudio) {
      // Áudio direto: usa o blob original.
      const dur = await probeDuration(file).catch(() => 0);
      setPrepared({
        blob: file,
        durationSec: Math.max(1, Math.round(dur)),
        sourceKind: "audio",
        originalSize: file.size,
        finalSize: file.size,
        filename: file.name
      });
      setPhase("form");
      return;
    }

    // Vídeo: extrai áudio localmente (vídeo original não sai do device).
    setPhase("extract");
    setExtractProgress(5);
    try {
      const buf = await file.arrayBuffer();
      setExtractProgress(20);
      const wav = await extractAudioAsWav(buf, (pct) => {
        setExtractProgress(20 + Math.floor(pct * 0.7));
      });
      setExtractProgress(95);
      const dur = await probeDuration(file).catch(() => wav.durationSec);
      setPrepared({
        blob: wav.blob,
        durationSec: Math.max(1, Math.round(dur)),
        sourceKind: "video",
        originalSize: file.size,
        finalSize: wav.blob.size,
        filename: file.name
      });
      setExtractProgress(100);
      setPhase("form");
    } catch (e) {
      setErrMsg(
        `Não consegui extrair áudio desse vídeo: ${e instanceof Error ? e.message : String(e)}. Tente exportar como mp3/wav e subir o áudio direto.`
      );
      setPhase("error");
    }
  }

  function onDrop(ev: DragEvent) {
    ev.preventDefault();
    setDragOver(false);
    const f = ev.dataTransfer?.files?.[0];
    if (f) void handleFile(f);
  }

  function onPick(ev: Event) {
    const t = ev.target as HTMLInputElement;
    const f = t.files?.[0];
    if (f) void handleFile(f);
  }

  async function handleSubmit() {
    if (!prepared) return;
    setPhase("upload");
    setUploadProgress(5);
    try {
      const input: CreateLectureInput = {
        title: form.title.trim() || `Importado ${new Date().toLocaleDateString("pt-BR")}`,
        kind: form.kind,
        theme: form.theme.trim() || undefined,
        language: form.language,
        summaryFocus: form.summaryFocus,
        captureKind: "audio_only",
        audioSources: prepared.sourceKind === "video" ? ["video-extract"] : ["upload"],
        durationSeconds: prepared.durationSec,
        sizeBytes: prepared.finalSize
      };
      setUploadProgress(15);
      const { lecture } = await api.createLecture(input);
      setUploadProgress(40);
      await api.transcribeLecture(lecture.id, prepared.blob, prepared.durationSec);
      setUploadProgress(100);
      setPhase("done");
      setTimeout(() => onSuccess(lecture.id), 400);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? `${e.message}${e.status ? ` (${e.status})` : ""}`
          : friendlyError(e);
      setErrMsg(msg);
      setPhase("error");
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onKeyDown={(e) => {
        if (e.key === "Escape" && phase !== "upload" && phase !== "extract") {
          onClose();
        }
      }}
    >
      <div class="relative w-full max-w-2xl rounded-lg border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between border-b border-border px-5 py-3">
          <div class="flex items-center gap-2">
            <Upload className="size-4 text-violet-600" />
            <h2 class="text-sm font-bold">Importar áudio ou vídeo pra transcrever</h2>
          </div>
          <button
            type="button"
            disabled={phase === "upload" || phase === "extract"}
            onClick={onClose}
            class="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="space-y-4 p-5">
          {phase === "pick" && (
            <PickStep
              dragOver={dragOver}
              setDragOver={setDragOver}
              inputRef={inputRef}
              onDrop={onDrop}
              onPick={onPick}
            />
          )}

          {phase === "extract" && (
            <div class="space-y-3 rounded-md border border-border bg-background/50 p-5">
              <div class="flex items-center gap-2 text-sm font-semibold">
                <Loader2 className="size-4 animate-spin text-violet-600" />
                Extraindo áudio do vídeo localmente…
              </div>
              <p class="text-xs text-muted-foreground">
                O vídeo NÃO sai do seu dispositivo. Só a trilha de áudio
                (compactada para 16kHz mono) será enviada pra transcrição.
              </p>
              <ProgressBar pct={extractProgress} />
            </div>
          )}

          {phase === "form" && prepared && (
            <FormStep
              form={form}
              setForm={setForm}
              prepared={prepared}
              onSubmit={handleSubmit}
              onCancel={() => {
                setPrepared(null);
                setPhase("pick");
              }}
            />
          )}

          {phase === "upload" && (
            <div class="space-y-3 rounded-md border border-border bg-background/50 p-5">
              <div class="flex items-center gap-2 text-sm font-semibold">
                <Loader2 className="size-4 animate-spin text-violet-600" />
                Enviando + transcrevendo…
              </div>
              <p class="text-xs text-muted-foreground">
                A IA está ouvindo o áudio agora. Pode levar 30s-2min dependendo
                da duração. Fique nessa janela ou volte mais tarde — você será
                notificado quando ficar pronto.
              </p>
              <ProgressBar pct={uploadProgress} />
            </div>
          )}

          {phase === "done" && (
            <div class="space-y-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 p-5 text-sm">
              <div class="flex items-center gap-2 font-semibold text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="size-4" />
                Transcrição pronta!
              </div>
              <p class="text-xs text-muted-foreground">
                Abrindo gravação…
              </p>
            </div>
          )}

          {phase === "error" && (
            <div class="space-y-3 rounded-md border border-destructive/40 bg-destructive/5 p-5">
              <div class="flex items-center gap-2 text-sm font-semibold text-destructive">
                <AlertCircle className="size-4" />
                Não foi possível processar
              </div>
              <p class="text-xs text-muted-foreground">{errMsg ?? "Erro desconhecido."}</p>
              <button
                type="button"
                onClick={() => {
                  setErrMsg(null);
                  setPrepared(null);
                  setPhase("pick");
                }}
                class="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted"
              >
                Tentar de novo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PickStep({
  dragOver,
  setDragOver,
  inputRef,
  onDrop,
  onPick
}: {
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
  inputRef: { current: HTMLInputElement | null };
  onDrop: (ev: DragEvent) => void;
  onPick: (ev: Event) => void;
}) {
  return (
    <>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        class={`flex flex-col items-center justify-center gap-3 rounded-md border-2 border-dashed p-10 text-center transition-colors ${
          dragOver
            ? "border-violet-500 bg-violet-500/10"
            : "border-border bg-background/50 hover:border-violet-500/60"
        }`}
      >
        <div class="flex items-center gap-3 text-violet-600">
          <FileAudio className="size-7" />
          <span class="text-muted-foreground">ou</span>
          <Film className="size-7" />
        </div>
        <div class="text-sm font-semibold">
          Arraste um arquivo de áudio ou vídeo aqui
        </div>
        <div class="text-xs text-muted-foreground">
          Áudio: mp3, wav, m4a, ogg, webm, opus. Vídeo: mp4, mov, webm.
          <br />
          Máx. 100MB. O vídeo nunca sai do seu dispositivo — extraímos só o
          áudio aqui no browser.
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          class="mt-2 rounded-md bg-violet-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-violet-700"
        >
          Escolher arquivo
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="audio/*,video/*"
          onChange={onPick}
          aria-label="Selecionar arquivo de áudio ou vídeo"
          title="Selecionar arquivo de áudio ou vídeo"
          class="hidden"
        />
      </div>
      <p class="text-[11px] leading-relaxed text-muted-foreground">
        Após escolher o arquivo, você confirma o título e o tipo de conteúdo
        antes de começar a transcrição.
      </p>
    </>
  );
}

function FormStep({
  form,
  setForm,
  prepared,
  onSubmit,
  onCancel
}: {
  form: FormState;
  setForm: (f: FormState) => void;
  prepared: PreparedAudio;
  onSubmit: () => void;
  onCancel: () => void;
}) {
  const sizeKB = (prepared.finalSize / 1024 / 1024).toFixed(1);
  const origKB = (prepared.originalSize / 1024 / 1024).toFixed(1);
  const dur = formatDuration(prepared.durationSec);
  return (
    <>
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-[11px] text-violet-700 dark:text-violet-300">
        {prepared.sourceKind === "video" ? (
          <>
            <strong>Vídeo:</strong> {prepared.filename} ({origKB}MB) →
            áudio extraído localmente ({sizeKB}MB · {dur}). Vídeo original
            descartado, nunca foi enviado.
          </>
        ) : (
          <>
            <strong>Áudio:</strong> {prepared.filename} ({sizeKB}MB · {dur}).
          </>
        )}
      </div>

      <Field label="Título">
        <input
          type="text"
          value={form.title}
          onInput={(e) =>
            setForm({ ...form, title: (e.target as HTMLInputElement).value })
          }
          class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-violet-500"
          placeholder="Ex: Aula 3 — Sistema cardiovascular"
        />
      </Field>

      <div class="grid grid-cols-2 gap-3">
        <Field label="Tipo de conteúdo">
          <select
            value={form.kind}
            title="Tipo de conteúdo"
            onChange={(e) =>
              setForm({ ...form, kind: (e.target as HTMLSelectElement).value as LectureKind })
            }
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-violet-500"
          >
            <option value="aula">Aula</option>
            <option value="palestra">Palestra</option>
            <option value="reuniao">Reunião</option>
            <option value="entrevista">Entrevista</option>
            <option value="estudo">Sessão de estudo</option>
            <option value="outro">Outro</option>
          </select>
        </Field>

        <Field label="Idioma">
          <select
            value={form.language}
            title="Idioma do áudio"
            onChange={(e) =>
              setForm({ ...form, language: (e.target as HTMLSelectElement).value })
            }
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-violet-500"
          >
            <option value="pt-BR">Português (BR)</option>
            <option value="en-US">Inglês (US)</option>
            <option value="es-ES">Espanhol</option>
            <option value="fr-FR">Francês</option>
          </select>
        </Field>
      </div>

      <Field label="Tema / disciplina (opcional)">
        <input
          type="text"
          value={form.theme}
          onInput={(e) =>
            setForm({ ...form, theme: (e.target as HTMLInputElement).value })
          }
          class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-violet-500"
          placeholder="Ex: Cardiologia, Direito Tributário, História do Brasil"
        />
      </Field>

      <Field label="Foco do resumo">
        <select
          value={form.summaryFocus}
          title="Foco do resumo"
          onChange={(e) =>
            setForm({
              ...form,
              summaryFocus: (e.target as HTMLSelectElement).value as LectureFocus
            })
          }
          class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-violet-500"
        >
          <option value="detailed_notes">Anotações detalhadas (caderno completo)</option>
          <option value="executive">Resumo executivo (3-5 pontos)</option>
          <option value="glossary">Glossário de termos</option>
          <option value="exam_questions">Perguntas de prova (8-15 questões)</option>
        </select>
      </Field>

      <div class="flex items-center justify-end gap-2 border-t border-border pt-3">
        <button
          type="button"
          onClick={onCancel}
          class="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted"
        >
          Trocar arquivo
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={!form.title.trim()}
          class="inline-flex items-center gap-1.5 rounded-md bg-violet-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Upload className="size-3.5" />
          Transcrever agora
        </button>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}

function ProgressBar({ pct }: { pct: number }) {
  return (
    <div class="h-1.5 w-full overflow-hidden rounded-full bg-muted">
      <div
        class="h-full bg-violet-600 transition-all"
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

function formatDuration(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ────────────────────────────────────────────────────────────────────────
// Audio extraction — vídeo (ou áudio) → AudioBuffer → WAV mono 16kHz
// ────────────────────────────────────────────────────────────────────────

async function extractAudioAsWav(
  arrayBuffer: ArrayBuffer,
  onProgress: (pct: number) => void
): Promise<{ blob: Blob; durationSec: number }> {
  onProgress(0);
  // Decode com AudioContext nativo (aceita qualquer formato que o browser
  // saiba reproduzir — webm, mp4, mov, mp3, etc.). Extrai só áudio.
  const tempCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  let decoded: AudioBuffer;
  try {
    decoded = await tempCtx.decodeAudioData(arrayBuffer.slice(0));
  } finally {
    void tempCtx.close().catch(() => {});
  }
  onProgress(40);

  // Downsample/mixdown pra 16kHz mono usando OfflineAudioContext (não toca
  // em tempo real — corre tão rápido quanto a CPU aguenta).
  const targetRate = 16000;
  const offline = new OfflineAudioContext(
    1,
    Math.max(1, Math.ceil(decoded.duration * targetRate)),
    targetRate
  );
  const source = offline.createBufferSource();
  source.buffer = decoded;
  source.connect(offline.destination);
  source.start();
  const rendered = await offline.startRendering();
  onProgress(80);

  const blob = encodeWav(rendered);
  onProgress(100);
  return { blob, durationSec: decoded.duration };
}

/** Encoda um AudioBuffer mono 16kHz em WAV (PCM 16-bit). */
function encodeWav(buffer: AudioBuffer): Blob {
  const numChannels = 1;
  const sampleRate = buffer.sampleRate;
  const channel = buffer.getChannelData(0);
  const numFrames = channel.length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numFrames * blockAlign;
  const bufferSize = 44 + dataSize;
  const ab = new ArrayBuffer(bufferSize);
  const view = new DataView(ab);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 8 * bytesPerSample, true);
  writeString(view, 36, "data");
  view.setUint32(40, dataSize, true);

  // PCM 16-bit little-endian
  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    const s = Math.max(-1, Math.min(1, channel[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }
  return new Blob([ab], { type: "audio/wav" });
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

/** Lê duração de um arquivo de mídia via HTMLMediaElement. */
function probeDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const isVideo = file.type.startsWith("video/");
    const el = (isVideo
      ? document.createElement("video")
      : document.createElement("audio")) as HTMLMediaElement;
    el.preload = "metadata";
    const url = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(url);
    el.addEventListener("loadedmetadata", () => {
      const d = el.duration;
      cleanup();
      if (Number.isFinite(d) && d > 0) resolve(d);
      else reject(new Error("metadata sem duration"));
    });
    el.addEventListener("error", () => {
      cleanup();
      reject(new Error("Falha ao ler metadados do arquivo."));
    });
    el.src = url;
  });
}
